export const PRODUCT_SCOPE = {
  PRIMARY_LAUNCH: 'primary_launch',
  SUPPORTING_COMPARISON: 'supporting_comparison',
};

export const LINK_TYPE = {
  GENIUSLINK: 'geniuslink',
  RETAILER: 'retailer',
};

export const GENIUSLINK_CTA = 'Check current price';

export const LAUNCH_PAGE_TYPE = 'seasonal_launch';

export function formatLaunchPrice(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return `$${value.toFixed(2)}`;
}

export function isGeniuslinkUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const host = new URL(url).hostname.replace(/^www\./i, '').toLowerCase();
    return host === 'at.fish';
  } catch {
    return false;
  }
}

export function commerceCtaLabel({ affiliateUrl, retailerName, ctaLabel } = {}) {
  if (ctaLabel) return ctaLabel;
  if (retailerName) return `Check price at ${retailerName}`;
  if (isGeniuslinkUrl(affiliateUrl)) return GENIUSLINK_CTA;
  return GENIUSLINK_CTA;
}

export function commerceLinkType(affiliateUrl) {
  return isGeniuslinkUrl(affiliateUrl) ? LINK_TYPE.GENIUSLINK : LINK_TYPE.RETAILER;
}

export function getLaunchProducts(launch, scope) {
  const products = Array.isArray(launch?.products) ? launch.products : [];
  if (!scope) return products;
  return products.filter((product) => product.scope === scope);
}

export function getPrimaryLaunchProducts(launch) {
  return getLaunchProducts(launch, PRODUCT_SCOPE.PRIMARY_LAUNCH);
}

export function getSupportingProducts(launch) {
  return getLaunchProducts(launch, PRODUCT_SCOPE.SUPPORTING_COMPARISON);
}

export function getLaunchProduct(launch, id) {
  return getLaunchProducts(launch).find((product) => product.id === id) || null;
}

export function getLaunchIndexGroups(launch) {
  const groups = launch?.launchIndex?.groups;
  if (!Array.isArray(groups)) return [];
  return groups
    .map((group) => ({
      id: group.id,
      label: group.label,
      countLabel: group.countLabel,
      products: (group.productIds || [])
        .map((id) => getLaunchProduct(launch, id))
        .filter(Boolean),
    }))
    .filter((group) => group.products.length);
}

export function getLaunchIndexProducts(launch) {
  return getLaunchIndexGroups(launch).flatMap((group) => group.products);
}

export function getLaunchImage(entity) {
  return entity?.image?.src ? entity.image : null;
}

export function getLaunchBrandAccent(launch, fallback = '#0F50F2') {
  const accent = launch?.visual?.brandAccent;
  return typeof accent === 'string' && accent.trim() ? accent.trim() : fallback;
}

export function getLaunchPath(launch) {
  if (!launch?.slug) return '';
  const base = launch.sectionBasePath || '/ice-fishing';
  return `${base.replace(/\/+$/, '')}/${launch.slug}`;
}

export function getLaunchQrPath(launch) {
  const path = getLaunchPath(launch);
  return path ? `${path}/qr` : '';
}

export function youtubeVideoId(launch) {
  if (launch?.videoId) return String(launch.videoId).trim() || null;
  const raw = launch?.videoUrl;
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = url.pathname.replace(/^\//, '').split('/')[0];
      return id || null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      if (url.pathname.startsWith('/embed/')) {
        return url.pathname.split('/')[2] || null;
      }
      return url.searchParams.get('v') || null;
    }
  } catch {
    return null;
  }
  return null;
}

export function collectVisibleLaunchCopy(launch) {
  const values = [];
  const walk = (value) => {
    if (value == null) return;
    if (typeof value === 'string') {
      values.push(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value === 'object') {
      Object.values(value).forEach(walk);
    }
  };
  walk(launch);
  return values;
}

export function hasEmDash(value) {
  const text = String(value || '');
  return text.includes(String.fromCharCode(0x2014)) || text.includes(String.fromCharCode(0x2013));
}
